import get from "lodash.get";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import useSubmitAnswer from "src/hooks/useObservationForm/useSubmitAnswer/useSubmitAnswer";
import { useAzureImageDownload } from "../AssetUploaders/useAzureImageDownload";
import { Button } from "src/components/ui/button";
import { Loader2, Check } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import Lightbox from "yet-another-react-lightbox";
import Download from "yet-another-react-lightbox/plugins/download";
import "yet-another-react-lightbox/styles.css";
import "src/features/SocialFeedContent/components/SocialPost/lightbox-overrides.css";
import axios from "axios";

import {
	DateQuestion,
	DropDownQuestion,
	EmailRecipientQuestion,
	RadioQuestion,
	TextQuestion,
	ImageUploadQuestion
} from "src/features/observations/components";
import { EmployeeSelectionQuestion } from "src/features/observations/components/EmployeeSelectionQuestion";

interface ObservationQuestionProps extends ObservationQuestion {
	observationType: IObservationType;

	onAnswerChange: (question: ObservationQuestion) => void;
}

const ObservationQuestion = (props: ObservationQuestionProps) => {
	const { questionNumber, questionType, id, observationType } = props;
	const { control } = useFormContext<ObservationFormState>();

	const { errors } = useFormState({ control });
	const { isDisabled } = useWatch({ control });

	const images = useWatch({
		control,
		name: `questions.${questionNumber - 1}.images`
	});

	const watchedQuestion = useWatch({
		control,
		name: `questions.${questionNumber - 1}`
	});

	const givenAnswer = useWatch({
		control,
		name: `questions.${questionNumber - 1}.givenAnswer`
	});

	useEffect(() => {
		if (!watchedQuestion) return;

		if (givenAnswer || images) props.onAnswerChange(watchedQuestion);
	}, [givenAnswer, images]);

	const { loading: isSubmittingAnswer, retrySave } = useSubmitAnswer({
		questionType,
		questionNumber,
		id,
		observationType
	});

	const error = get(errors, ["questions", questionNumber - 1], null);

	return (
		<ObservationQuestionLayout
			isDisabled={!!isDisabled}
			error={error}
			questionNumber={questionNumber}
			note={props.note}
			questionType={questionType}
			observationAnswers={props.observationAnswers}
			question={props.question}
			isLoading={isSubmittingAnswer}
			order={props.order}
			image={props.imageCoverId ? props.imageCoverId : undefined}
			showAction={Boolean(props.isActionRequired)}
			observationType={observationType}
			images={images}
			retrySave={retrySave}
		/>
	);
};

interface ObservationQuestionLayoutProps {
	isDisabled: boolean;
	error: any;
	questionNumber: number;
	note: string;
	questionType: IQuestionTypeEnum | null;
	observationAnswers: IObservationAnswer[];
	question: string;
	order: number;
	isLoading: boolean;
	showErrorBorder?: boolean;
	formStatic?: boolean;
	image?: string;
	images?: string[];
	showAction?: boolean;
	observationType?: IObservationType;
	showImageUploadButton?: boolean;
	retrySave?: () => Promise<void>;
}

const ObservationQuestionLayout = ({
	isDisabled,
	error,
	showErrorBorder = false,
	questionNumber,
	note,
	questionType,
	observationAnswers,
	question,
	isLoading,
	image,
	images,
	formStatic,
	showAction,
	showImageUploadButton = true,
	retrySave
}: ObservationQuestionLayoutProps) => {
	const isQuestionsHeading = questionType === "HEADING";

	const { url: headingCover, error: headingCoverError } = useAzureImageDownload(
		"image",
		"",
		image,
		"retail-app-observation-heading"
	);

	const { url: headingCoverFull } = useAzureImageDownload(
		"image",
		"",
		image,
		"retail-app-observation-heading",
		"full"
	);

	const isImageReady = isQuestionsHeading && headingCover && !headingCoverError;
	const lightboxPortalRef = useRef<HTMLDivElement>(null);
	const [headingLightboxOpen, setHeadingLightboxOpen] = useState(false);

	let errorMessage = null;

	if (error && !isQuestionsHeading) {
		if (questionType === "IMAGE_UPLOAD")
			errorMessage = error.images?.message ?? "failed to upload image";
		else errorMessage = error.givenAnswer?.message ?? "unexpected error";
	}

	const onRetryClick = () => {
		if (retrySave) retrySave();
	};

	const hasBorderError = !isQuestionsHeading && (error || showErrorBorder);

	const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">(
		"idle"
	);
	const userEdited = useRef(false);

	useEffect(() => {
		if (isLoading && userEdited.current) {
			setSaveState("saving");
		} else if (!isLoading && saveState === "saving") {
			setSaveState("saved");
		}
	}, [isLoading]);

	// Track user interaction via pointer/keyboard events on this card
	const handleUserInteraction = () => {
		userEdited.current = true;
	};

	return (
		<div
			role="group"
			className={`relative rounded-xl border bg-white ${hasBorderError ? "border-destructive" : "border-transparent"}`}
			aria-label={`question-${questionNumber}`}
			aria-disabled={isDisabled}
			onClickCapture={handleUserInteraction}
			onKeyDownCapture={handleUserInteraction}
		>
			{/* Save state indicator */}
			{saveState === "saving" && (
				<div className="absolute top-4 right-4">
					<Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
				</div>
			)}
			{saveState === "saved" && !isLoading && (
				<div className="absolute top-4 right-4">
					<Check className="h-5 w-5 text-green-700" />
				</div>
			)}

			{errorMessage && (
				<div className="relative">
					<span className="absolute -top-3 right-3 rounded-sm bg-destructive text-white px-2 py-0.5 text-xs">
						{errorMessage}
					</span>
				</div>
			)}

			<div className="p-6">
				<div className="flex-1">
					<label
						id={`question-${questionNumber}-label`}
						className="block font-heading text-lg font-medium mb-4"
						htmlFor={`question-${questionNumber}`}
					>
						{question}
					</label>

					<p className={isDisabled ? "text-muted-foreground" : ""}>{note}</p>

					{isImageReady && (
						<div className="my-4">
							{formStatic ? (
								<button
									type="button"
									aria-label="View heading image"
									onClick={() => setHeadingLightboxOpen(true)}
									className="block cursor-pointer bg-transparent border-0 p-0"
								>
									<img
										src={headingCover}
										alt="heading background"
										className="max-w-full rounded-lg"
									/>
								</button>
							) : (
								<img
									src={headingCover}
									alt="heading background"
									className="max-w-full rounded-lg"
								/>
							)}
							{formStatic && (
								<>
									<div ref={lightboxPortalRef} />
									<Lightbox
										open={headingLightboxOpen}
										index={0}
										close={() => setHeadingLightboxOpen(false)}
										slides={[
											{
												src: headingCoverFull || headingCover,
												download: {
													url: headingCoverFull || headingCover,
													filename: `heading-${image}`
												}
											}
										]}
										portal={{
											root: lightboxPortalRef.current ?? undefined
										}}
										carousel={{ finite: true }}
										render={{
											buttonPrev: () => null,
											buttonNext: () => null
										}}
										styles={{
											container: {
												backgroundColor: "rgba(0, 0, 0, 0.85)"
											},
											slide: { padding: "40px" }
										}}
										plugins={[Download]}
										download={{
											download: async ({ slide }) => {
												if (!slide.src) return;
												try {
													const response = await axios.get(slide.src, {
														responseType: "blob"
													});
													const objectUrl = URL.createObjectURL(response.data);
													const link = document.createElement("a");
													link.href = objectUrl;
													link.download =
														("download" in slide &&
														typeof slide.download === "object"
															? slide.download?.filename
															: undefined) ?? "image";
													document.body.appendChild(link);
													link.click();
													document.body.removeChild(link);
													URL.revokeObjectURL(objectUrl);
												} catch {
													window.open(slide.src, "_blank");
												}
											}
										}}
									/>
								</>
							)}
						</div>
					)}

					{headingCoverError && (
						<div className="my-4 mx-8">
							<p className="text-red-500 font-bold">
								Sorry! We are having trouble loading the header image.
							</p>
						</div>
					)}

					{!isQuestionsHeading && (
						<>
							<div className="mb-4">
								{questionType === "RADIO" && (
									<RadioQuestion
										name={`questions.${questionNumber - 1}.givenAnswer`}
										questionNumber={questionNumber}
										answers={observationAnswers}
										staticInput={formStatic}
										showAction={showAction}
										rules={{ required: "Please enter an answer" }}
									/>
								)}

								{questionType === "TEXT" && (
									<TextQuestion
										name={`questions.${questionNumber - 1}.givenAnswer`}
										questionNumber={questionNumber}
										staticInput={formStatic}
										showAction={showAction}
										rules={{ required: "Please enter an answer" }}
									/>
								)}

								{questionType === "SELECT" && (
									<DropDownQuestion
										name={`questions.${questionNumber - 1}.givenAnswer`}
										questionNumber={questionNumber}
										answers={observationAnswers}
										staticInput={formStatic}
										showAction={showAction}
										rules={{ required: "Please enter an answer" }}
									/>
								)}

								{(questionType === "DATE" ||
									questionType === "OBSERVATION_DATE") && (
									<DateQuestion
										name={`questions.${questionNumber - 1}.givenAnswer`}
										questionNumber={questionNumber}
										staticInput={formStatic}
										selectFutureDates={questionType !== "OBSERVATION_DATE"}
										showAction={showAction}
										rules={{ required: "Please enter an answer" }}
									/>
								)}

								{questionType === "CC_EMAIL_RECIPIENT" && (
									<EmailRecipientQuestion
										name={`questions.${questionNumber - 1}.givenAnswer`}
										questionNumber={questionNumber}
										staticInput={formStatic}
										showAction={showAction}
									/>
								)}

								{questionType === "IMAGE_UPLOAD" && (
									<div className="mt-8">
										<ImageUploadQuestion
											name={`questions.${questionNumber - 1}.images`}
											images={images}
											questionNumber={questionNumber}
											staticInput={formStatic}
											showAction={showAction}
											showUploadButton={showImageUploadButton}
											rules={{ required: "Please upload image(s)" }}
										/>
									</div>
								)}

								{questionType === "EMPLOYEE_SELECTION" && (
									<div className="mt-2">
										<EmployeeSelectionQuestion
											name={`questions.${questionNumber - 1}.givenAnswer`}
											questionNumber={questionNumber}
											staticInput={formStatic}
											showAction={showAction}
											rules={{ required: "Please enter an answer" }}
										/>
									</div>
								)}
							</div>

							{error &&
								!isLoading &&
								error.givenAnswer &&
								error.givenAnswer.type === "networkIssue" && (
									<div className="flex items-center justify-end">
										<Button
											variant="outline"
											className="bg-white font-normal"
											disabled={isLoading}
											onClick={onRetryClick}
										>
											Retry submitting
										</Button>
									</div>
								)}
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export {
	ObservationQuestion,
	ObservationQuestionLayout,
	type ObservationQuestionProps,
	type ObservationQuestionLayoutProps
};
